import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import BranchesMap from '../about/components/BranchesMap';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    etablissement: '',
    sujet: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams({
        nom: formData.nom,
        email: formData.email,
        telephone: formData.telephone,
        etablissement: formData.etablissement,
        sujet: formData.sujet,
        message: formData.message,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d5co60vdcu97hjrn42h0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        // Envoyer un email de confirmation à l'expéditeur
        await sendConfirmationEmail(formData);
        
        setShowSuccess(true);
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          etablissement: '',
          sujet: '',
          message: '',
        });
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du formulaire:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendConfirmationEmail = async (data: typeof formData) => {
    try {
      // Créer le contenu de l'email de confirmation
      const emailContent = `
Bonjour ${data.nom},

Nous avons bien reçu votre message et nous vous remercions de nous avoir contactés.

Voici un récapitulatif de votre demande :

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
INFORMATIONS DE CONTACT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Nom : ${data.nom}
Email : ${data.email}
Téléphone : ${data.telephone}
${data.etablissement ? `Établissement : ${data.etablissement}` : ''}
Sujet : ${data.sujet}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
VOTRE MESSAGE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${data.message}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Notre équipe vous répondra dans les plus brefs délais, généralement sous 24 heures ouvrées.

Si votre demande est urgente, n'hésitez pas à nous contacter directement :
📞 Téléphone : +213 770 30 79 05
📧 Email : contact@adfbiomedical.dz
📍 Adresse : Local N01 Benghazi N16, Local N01 Benghazi N16, Baraki, Alger, Algérie

Cordialement,
L'équipe ADF Biomédical

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ADF Biomédical - Votre partenaire en dispositifs médicaux
www.adfbiomedical.dz
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      `.trim();

      // Envoyer l'email via un service (exemple avec une API d'envoi d'email)
      const emailBody = new URLSearchParams({
        to: data.email,
        subject: `Confirmation de réception - ${data.sujet}`,
        message: emailContent,
        from: 'ADF Biomédical <contact@adfbiomedical.dz>',
      }).toString();

      await fetch('https://readdy.ai/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: emailBody,
      });
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email de confirmation:', error);
      // Ne pas bloquer le processus si l'email de confirmation échoue
    }
  };

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      title: 'Adresse',
      content: 'Local N01 Benghazi N16, Local N01 Benghazi N16, Baraki, Alger, Algérie',
    },
    {
      icon: 'ri-phone-line',
      title: 'Téléphone',
      content: '+213 770 30 79 05',
      link: 'tel:+213770307905',
    },
    {
      icon: 'ri-mail-line',
      title: 'Email',
      content: 'contact@adfbiomedical.dz',
      link: 'mailto:contact@adfbiomedical.dz',
    },
    {
      icon: 'ri-time-line',
      title: 'Horaires',
      content: 'Lundi - Vendredi : 08h00 - 17h00',
    },
  ];

  return (
    <>
      <header>
        <h1 className="sr-only">Contactez ADF Biomédical - Fournisseur de dispositifs médicaux en Algérie</h1>
      </header>
      <Navbar />
      <main>
        <section className="relative h-72 sm:h-80 md:h-96 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://public.readdy.ai/ai/img_res/755d8d2da50e3fc246419d784ad4b955.jpg"
              alt="Contact ADF Biomédical"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
          </div>
          <div className="relative z-10 text-center px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-white/80 text-xs sm:text-sm mb-3 sm:mb-4">
                <Link to="/" className="hover:text-primary transition-colors cursor-pointer">Accueil</Link> / Contact
              </p>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 md:mb-6">Contactez-nous</h2>
              <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
                Notre équipe est à votre disposition pour répondre à toutes vos questions
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-16 sm:py-20 md:py-24 bg-bg-light">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h3 className="text-3xl sm:text-4xl font-bold text-text-primary mb-6 md:mb-8">Informations de contact</h3>
                <p className="text-base sm:text-lg text-text-secondary leading-relaxed mb-8 md:mb-12">
                  N'hésitez pas à nous contacter pour toute demande d'information, devis ou assistance technique. Notre équipe d'experts est à votre écoute.
                </p>

                <div className="space-y-6 md:space-y-8">
                  {contactInfo.map((info, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start"
                    >
                      <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-primary/10 rounded-full flex-shrink-0">
                        <i className={`${info.icon} text-xl sm:text-2xl text-primary`}></i>
                      </div>
                      <div className="ml-4 sm:ml-6">
                        <h4 className="text-base sm:text-lg font-semibold text-text-primary mb-1 sm:mb-2">{info.title}</h4>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-sm sm:text-base text-text-secondary hover:text-primary transition-colors duration-300 cursor-pointer break-words"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-sm sm:text-base text-text-secondary break-words">{info.content}</p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="mt-8 md:mt-12 bg-white rounded-2xl p-6 md:p-8 shadow-lg"
                >
                  <h4 className="text-lg sm:text-xl font-semibold text-text-primary mb-3 sm:mb-4">Localisation</h4>
                  <div className="w-full h-48 sm:h-56 md:h-64 rounded-xl overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3197.4!2d3.086472!3d36.737232!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDQ0JzE0LjAiTiAzwrAwNScxMS4zIkU!5e0!3m2!1sfr!2sdz!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Localisation ADF Biomédical"
                    ></iframe>
                  </div>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="bg-white rounded-2xl shadow-lg p-6 sm:p-8"
              >
                <h3 className="text-2xl sm:text-3xl font-bold text-text-primary mb-6 md:mb-8">Envoyez-nous un message</h3>

                <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                  <div className="space-y-5 sm:space-y-6">
                    <div>
                      <label htmlFor="nom" className="block text-xs sm:text-sm font-medium text-text-primary mb-2">
                        Nom complet <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="nom"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors duration-300 text-xs sm:text-sm"
                        placeholder="Votre nom complet"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                      <div>
                        <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-text-primary mb-2">
                          Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors duration-300 text-xs sm:text-sm"
                          placeholder="votre@email.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="telephone" className="block text-xs sm:text-sm font-medium text-text-primary mb-2">
                          Téléphone <span className="text-danger">*</span>
                        </label>
                        <input
                          type="tel"
                          id="telephone"
                          name="telephone"
                          value={formData.telephone}
                          onChange={handleChange}
                          required
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors duration-300 text-xs sm:text-sm"
                          placeholder="+213 XXX XX XX XX"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="etablissement" className="block text-xs sm:text-sm font-medium text-text-primary mb-2">
                        Établissement
                      </label>
                      <input
                        type="text"
                        id="etablissement"
                        name="etablissement"
                        value={formData.etablissement}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors duration-300 text-xs sm:text-sm"
                        placeholder="Nom de votre établissement"
                      />
                    </div>

                    <div>
                      <label htmlFor="sujet" className="block text-xs sm:text-sm font-medium text-text-primary mb-2">
                        Sujet <span className="text-danger">*</span>
                      </label>
                      <select
                        id="sujet"
                        name="sujet"
                        value={formData.sujet}
                        onChange={handleChange}
                        required
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors duration-300 text-xs sm:text-sm cursor-pointer"
                      >
                        <option value="">Sélectionnez un sujet</option>
                        <option value="Demande de devis">Demande de devis</option>
                        <option value="Information produit">Information produit</option>
                        <option value="Support technique">Support technique</option>
                        <option value="Partenariat">Partenariat</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-xs sm:text-sm font-medium text-text-primary mb-2">
                        Message <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        maxLength={500}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors duration-300 text-xs sm:text-sm resize-none"
                        placeholder="Votre message (max 500 caractères)"
                      ></textarea>
                      <p className="text-xs text-text-secondary mt-2">{formData.message.length}/500 caractères</p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-primary text-white rounded-lg font-semibold text-base sm:text-lg hover:bg-primary-dark transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CARTE INTERACTIVE DES SUCCURSALES */}
        <BranchesMap />

        {showSuccess && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-8 sm:p-12 max-w-md w-full text-center shadow-2xl"
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-success/10 rounded-full mx-auto mb-4 sm:mb-6">
                <i className="ri-check-line text-4xl sm:text-5xl text-success"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-text-primary mb-3 sm:mb-4">Message envoyé !</h3>
              <p className="text-sm sm:text-base text-text-secondary mb-6 sm:mb-8">
                Votre message a été envoyé avec succès. Notre équipe vous contactera dans les plus brefs délais.
              </p>
              <button
                onClick={() => setShowSuccess(false)}
                className="px-6 sm:px-8 py-2.5 sm:py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all duration-300 cursor-pointer whitespace-nowrap text-sm sm:text-base"
              >
                Fermer
              </button>
            </motion.div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
};

export default ContactPage;